import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Loader2, Search as SearchIcon } from 'lucide-react';
import SmartSearchPanel from './SmartSearchPanel';
import useSmartResults from './useSmartResults';
import useSmartSession from './useSmartSession';
import useSmartShortcut from './useSmartShortcut';
import { buildOptions, nextIndex } from './smartOptions';

const KBD = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '') ? '⌘K' : 'Ctrl K';
const MUTED = { color: 'var(--strata-text-muted)' };
const Kbd = ({ children }) => <kbd className="text-[9px] px-1 py-0.5 rounded border font-sans" style={{ ...MUTED, borderColor: 'var(--strata-border)' }}>{children}</kbd>;

// The header bar: clicking it, focusing it or typing in it opens the palette (it keeps the old search's test ids).
function Trigger({ onOpen }) {
  return (
    <form role="search" data-testid="topbar-search-form" onSubmit={(e) => e.preventDefault()} onClick={() => onOpen('')}
      className="relative flex items-center gap-2 px-3 py-1.5 rounded-md flex-1 max-w-sm cursor-text transition-shadow duration-150 hover:shadow-[0_0_0_1px_var(--strata-border)]"
      style={{ background: 'var(--strata-canvas-soft)' }}>
      <SearchIcon className="w-3.5 h-3.5 flex-shrink-0" style={MUTED} />
      <input data-testid="topbar-search" type="text" value="" placeholder="Search everything…" autoComplete="off" aria-haspopup="dialog"
        onFocus={() => onOpen('')} onChange={(e) => onOpen(e.target.value)} className="bg-transparent border-0 outline-none text-xs flex-1 cursor-text"
        style={{ color: 'var(--strata-text-primary)', fontFamily: 'var(--strata-font-body)' }} />
      <span className="hidden xl:inline" data-testid="topbar-search-kbd" title={`${KBD} or / to search`}><Kbd>{KBD}</Kbd></span>
    </form>
  );
}

function Palette({ text, setText, busy, onKeyDown, onClose, activeId, children }) {
  return createPortal(
    <div className="fixed inset-0 z-[55] print:hidden" data-testid="smart-search-overlay">
      <div className="absolute inset-0 bg-[#0F172A]/25 backdrop-blur-[2px] animate-in fade-in-0 duration-150" onMouseDown={onClose} data-testid="smart-search-backdrop" />
      <div role="dialog" aria-modal="true" aria-label="Search" data-testid="topbar-search-dropdown"
        className="absolute inset-x-0 top-0 sm:top-[10vh] sm:mx-auto sm:w-[min(680px,92vw)] bg-white sm:rounded-xl border shadow-[0_24px_64px_-16px_rgba(15,23,42,0.40)] overflow-hidden animate-in fade-in-0 slide-in-from-top-2 duration-150"
        style={{ borderColor: 'var(--strata-border)', fontFamily: 'var(--strata-font-body)' }}>
        <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: 'var(--strata-border)' }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin shrink-0" style={MUTED} data-testid="topbar-search-spinner" /> : <SearchIcon className="w-4 h-4 shrink-0 text-[#4575A8]" />}
          <input autoFocus data-testid="smart-search-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKeyDown}
            placeholder="Search jobs, estimates, leads, people…" autoComplete="off" role="combobox" aria-expanded="true"
            aria-controls="smart-search-list" aria-activedescendant={activeId} className="flex-1 bg-transparent outline-none text-[15px]"
            style={{ color: 'var(--strata-text-primary)' }} />
          <button type="button" onClick={onClose} data-testid="smart-search-close" aria-label="Close search"><Kbd>Esc</Kbd></button>
        </div>
        <div id="smart-search-list" role="listbox" className="max-h-[calc(100vh-7rem)] sm:max-h-[60vh] overflow-y-auto pb-2">{children}</div>
        <div className="hidden sm:flex items-center gap-3 px-4 py-2 border-t text-[10px]" style={{ ...MUTED, borderColor: 'var(--strata-border)' }}>
          <span><Kbd>↑</Kbd> <Kbd>↓</Kbd> move</span><span><Kbd>↵</Kbd> open</span><span><Kbd>Esc</Kbd> close</span>
          <span className="ml-auto">Your recent activity is only visible to you.</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// Smart search: Ctrl+K / "/" or a click opens it; recent activity first, then live results from every source this
// person can see, streaming in by type. Arrow keys + Enter pick a row; Esc closes.
export default function SmartSearch() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [activeKey, setActiveKey] = useState(null);
  const session = useSmartSession(open);
  const res = useSmartResults(open ? text : '', session.sources, session.minChars);
  const q = text.trim();
  const options = useMemo(() => buildOptions({ q, recents: session.recents, res, sources: session.sources }), [q, session.recents, res, session.sources]);
  const activeIndex = options.findIndex((o) => o.key === activeKey);
  useEffect(() => { setActiveKey(null); }, [q]);
  const openWith = useCallback((initial) => { setText(initial || ''); setOpen(true); }, []);
  useSmartShortcut(openWith);
  const close = () => { setOpen(false); setText(''); setActiveKey(null); };
  const pick = (o) => { close(); navigate(o.path); };
  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveKey(options[nextIndex(activeIndex, options.length, e.key === 'ArrowDown')]?.key || null);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const o = options[activeIndex] || options[0];
      if (o) pick(o);
    }
  };
  useEffect(() => { if (activeIndex >= 0) document.getElementById(`smart-search-opt-${activeIndex}`)?.scrollIntoView?.({ block: 'nearest' }); }, [activeIndex]);
  const busy = q.length >= session.minChars && (res.q !== q || Object.values(res.groups).some((g) => g.status === 'loading'));
  return (
    <>
      <Trigger onOpen={openWith} />
      {open && (
        <Palette text={text} setText={setText} busy={busy} onKeyDown={onKeyDown} onClose={close} activeId={activeIndex >= 0 ? `smart-search-opt-${activeIndex}` : undefined}>
          <SmartSearchPanel q={q} session={session} res={res} options={options} activeKey={activeKey} setActiveKey={setActiveKey} onPick={pick} />
        </Palette>
      )}
    </>
  );
}
